import type * as THREE from 'three';
import {
  cloneLinearComponentPatternPersistenceDocument,
  parseLinearComponentPatternPersistenceDocument,
  type LinearComponentPatternPersistenceDocument,
} from '../../pattern/LinearComponentPatternPersistence';
import {
  remapPuppyAssemblyNodeReferences,
  resolvePuppyAssemblyNodeReferenceAlias,
} from './remapPuppyAssemblyNodeReferences';
//------ 线性零部件阵列导入 BRep 稳定引用 开始------
import {
  createPuppyBodyBindingReferenceIndex,
  resolvePuppyExportedBodyBindingId,
  type PuppyBodyBindingReferenceIndex,
} from './puppyBodyBindingReferenceResolver';
import type { PuppyDisplayNodeBinding } from './types';
//------ 线性零部件阵列导入 BRep 稳定引用 结束------

/** assembly.json.extensions 中保存线性零部件阵列定义的字段名。 */
export const PUPPY_LINEAR_COMPONENT_PATTERN_EXTENSION_KEY = 'linearComponentPatterns';

/** 线性阵列状态中保存单个场景对象 ID 的字段。 */
const LINEAR_COMPONENT_PATTERN_OBJECT_ID_KEYS = [
  'direction1ReferenceObjectId',
  'direction1RotationAxisObjectId',
  'direction1ReferenceGeometryObjectId',
  'direction1SelectedReferenceObjectId',
  'direction2ReferenceObjectId',
  'direction2ReferenceGeometryObjectId',
  'direction2SelectedReferenceObjectId',
] as const;

/** 对象 ID 字段到稳定引用描述符字段的映射。 */
const LINEAR_COMPONENT_PATTERN_DESCRIPTOR_KEY_BY_OBJECT_ID_KEY: Readonly<Record<
  typeof LINEAR_COMPONENT_PATTERN_OBJECT_ID_KEYS[number],
  string
>> = {
  direction1ReferenceObjectId: 'direction1ReferenceDescriptor',
  direction1RotationAxisObjectId: 'direction1RotationAxisDescriptor',
  direction1ReferenceGeometryObjectId: 'direction1ReferenceGeometryDescriptor',
  direction1SelectedReferenceObjectId: 'direction1SelectedReferenceDescriptor',
  direction2ReferenceObjectId: 'direction2ReferenceDescriptor',
  direction2ReferenceGeometryObjectId: 'direction2ReferenceGeometryDescriptor',
  direction2SelectedReferenceObjectId: 'direction2SelectedReferenceDescriptor',
};

//------ 线性零部件阵列组件源精确物化 开始------
/**
 * 离线包线性阵列定义的运行时物化解析器。
 *
 * 设计说明：
 * 组件源必须解析到真实装配对象，方向等普通引用可以解析到选择代理；两者不能共用同一映射，
 * 否则零件节点的选择代理指向父装配时，会把整个 `imported-assembly` 当作阵列源递归克隆。
 */
export interface PuppyLinearComponentPatternMaterializationResolvers {
  /** 当前离线包文档的根装配节点 ID；根装配不能作为线性零部件阵列源。 */
  readonly documentRootAssemblyNodeId: string;
  /** 当前文档根装配在本次会话中的运行时对象 ID，用于拦截错误映射。 */
  readonly documentRootObjectId?: string | null;
  /** 将稳定装配节点 ID 解析为真实组件运行时对象 ID。 */
  readonly resolveComponentObjectId: (stableObjectId: string) => string | null;
  /** 将稳定场景引用 ID 解析为当前会话的引用或选择代理对象 ID。 */
  readonly resolveReferenceObjectId: (stableObjectId: string) => string | null;
}
//------ 线性零部件阵列组件源精确物化 结束------

/**
 * 创建离线包线性阵列定义扩展。
 *
 * 运行时 UUID 会被归一为 assembly.json 中的稳定节点 ID；阵列复制体 ID 不会进入扩展。
 *
 * @param input.document Manager 产生的定义型持久化文档。
 * @param input.sceneRoot 当前场景根；仅普通场景引用允许在缺少装配映射时回退到对象名称。
 * @param input.runtimeSourceObjectIdsByAssemblyNodeId 装配节点到运行时对象 ID 的导出映射。
 * @param input.nodeBindings 最终写入离线包的 display/body 绑定。
 * @returns 存在阵列定义时返回扩展；无阵列时返回 `undefined`。
 */
export function createPuppyLinearComponentPatternExtension(input: {
  readonly document: LinearComponentPatternPersistenceDocument | null | undefined;
  readonly sceneRoot: THREE.Object3D;
  readonly runtimeSourceObjectIdsByAssemblyNodeId: Readonly<Record<string, readonly string[]>>;
  readonly nodeBindings: readonly PuppyDisplayNodeBinding[];
}): LinearComponentPatternPersistenceDocument | undefined {
  if (!input.document || input.document.records.length === 0) {
    return undefined;
  }

  const stableObjectIdByRuntimeObjectId = createStableObjectIdIndex(
    input.runtimeSourceObjectIdsByAssemblyNodeId,
  );
  const stableAssemblyNodeIds = new Set([
    ...Object.keys(input.runtimeSourceObjectIdsByAssemblyNodeId),
    ...input.nodeBindings
      .map((nodeBinding) => nodeBinding.assemblyNodeId?.trim() ?? '')
      .filter((assemblyNodeId) => assemblyNodeId.length > 0),
  ]);
  const resolveStableObjectId = (runtimeObjectId: string): string => {
    // 已经稳定化的装配节点 ID 必须保持幂等，便于拓扑引用与普通对象引用共享解析入口。
    if (stableAssemblyNodeIds.has(runtimeObjectId)) {
      return runtimeObjectId;
    }

    const stableObjectId = stableObjectIdByRuntimeObjectId.get(runtimeObjectId);
    if (stableObjectId) {
      return stableObjectId;
    }

    // 原生 CAD 对象可能没有装配节点映射；非空且唯一的名称可作为 Viewer 的稳定回查兜底。
    const sceneObject = input.sceneRoot.getObjectByProperty('uuid', runtimeObjectId)
      ?? input.sceneRoot.getObjectByName(runtimeObjectId);
    const stableName = sceneObject?.name.trim() ?? '';
    if (stableName.length > 0) {
      return stableName;
    }
    throw new Error(`线性零部件阵列引用无法归一为离线包稳定对象 ID：${runtimeObjectId}。`);
  };
  const resolveStableComponentObjectId = (runtimeObjectId: string): string => {
    if (stableAssemblyNodeIds.has(runtimeObjectId)) {
      return runtimeObjectId;
    }

    const stableObjectId = stableObjectIdByRuntimeObjectId.get(runtimeObjectId);
    if (stableObjectId) {
      return stableObjectId;
    }

    // 阵列源必须是可移动 occurrence；场景名称会被 GLB 导出重命名，不能作为组件身份。
    throw new Error(
      `线性零部件阵列组件源无法归一为稳定装配节点：${runtimeObjectId}。`,
    );
  };

  //------ 线性零部件阵列导入 BRep 稳定引用 开始------
  const bodyBindingIndex = createPuppyBodyBindingReferenceIndex(input.nodeBindings);
  const normalizedDocument = normalizePatternAssemblyTopologyReferences(
    input.document,
    resolveStableObjectId,
    bodyBindingIndex,
  );
  return transformPatternObjectIds(
    normalizedDocument,
    resolveStableObjectId,
    resolveStableComponentObjectId,
  );
  //------ 线性零部件阵列导入 BRep 稳定引用 结束------
}

/**
 * 把离线包中的稳定装配节点 ID 物化为当前加载会话的运行时对象 ID。
 *
 * @param document 已解析的离线包线性阵列扩展。
 * @param resolvers 按组件源与普通引用职责拆分的运行时解析器。
 * @returns 可直接交给 Manager 恢复的定义文档。
 */
export function materializePuppyLinearComponentPatternDocument(
  document: LinearComponentPatternPersistenceDocument,
  resolvers: PuppyLinearComponentPatternMaterializationResolvers,
): LinearComponentPatternPersistenceDocument {
  //------ 线性零部件阵列组件源精确物化 开始------
  const documentRootAssemblyNodeId = resolvers.documentRootAssemblyNodeId.trim();
  const documentRootObjectId = resolvers.documentRootObjectId?.trim() ?? '';
  const resolveComponentObjectId = (stableObjectId: string): string => {
    if (stableObjectId === documentRootAssemblyNodeId) {
      throw new Error(
        `线性零部件阵列不能以当前文档根装配节点作为阵列源零部件：${stableObjectId}。`,
      );
    }

    const runtimeObjectId = resolvers.resolveComponentObjectId(stableObjectId) ?? stableObjectId;
    if (documentRootObjectId && runtimeObjectId === documentRootObjectId) {
      throw new Error(
        `线性零部件阵列组件引用错误映射到了当前文档根对象：${stableObjectId} -> ${runtimeObjectId}。`,
      );
    }
    return runtimeObjectId;
  };
  const resolveReferenceObjectId = (stableObjectId: string): string => (
    resolvers.resolveReferenceObjectId(stableObjectId) ?? stableObjectId
  );
  return transformPatternObjectIds(
    document,
    resolveReferenceObjectId,
    resolveComponentObjectId,
  );
  //------ 线性零部件阵列组件源精确物化 结束------
}

/**
 * 重映射线性阵列定义中的装配节点引用。
 *
 * @param input.document 原始定义文档。
 * @param input.aliasByNodeId 旧节点 ID 到新节点 ID 的确定性映射。
 * @returns 已同步普通对象引用与装配拓扑引用的定义文档。
 */
export function remapPuppyLinearComponentPatternNodeReferences(input: {
  readonly document: LinearComponentPatternPersistenceDocument;
  readonly aliasByNodeId: Readonly<Record<string, string>>;
}): LinearComponentPatternPersistenceDocument {
  const remappedTopologyDocument = remapPuppyAssemblyNodeReferences({
    value: input.document,
    aliasByNodeId: input.aliasByNodeId,
  });
  return transformPatternObjectIds(remappedTopologyDocument, (objectId) => (
    resolvePuppyAssemblyNodeReferenceAlias(objectId, input.aliasByNodeId)
  ));
}

/**
 * 按项目模型局部最终化结果重绑定阵列 imported BRep 拓扑 occurrence。
 *
 * 该入口只改写已存在的稳定拓扑引用及其兼容宿主字段，不解析场景对象，也不求解阵列。
 *
 * @param input.document 已完成节点 alias 重映射的阵列文档。
 * @param input.resolveBodyOccurrence 将单条来源拓扑引用解析为最终 occurrence 的回调。
 * @returns topology reference、hostObjectId 与兼容 objectId 已同步的独立文档。
 */
export function rebindPuppyLinearComponentPatternBodyOccurrences(input: {
  readonly document: LinearComponentPatternPersistenceDocument;
  readonly resolveBodyOccurrence: (context: {
    readonly featureId: string;
    readonly displayName: string;
    readonly reference: PuppyCAD.AssemblyTopologySelectionRef;
  }) => PuppyCAD.AssemblyTopologySelectionRef;
}): LinearComponentPatternPersistenceDocument {
  const document = cloneLinearComponentPatternPersistenceDocument(input.document);
  document.records.forEach((record) => {
    const state = record.definition.state as unknown as Record<string, unknown>;
    Object.entries(state).forEach(([descriptorKey, value]) => {
      if (!descriptorKey.endsWith('Descriptor') || !isRecord(value)) {
        return;
      }
      const topologyReference = value.assemblyTopologyRef;
      if (!isRecord(topologyReference)) {
        return;
      }

      const nextReference = input.resolveBodyOccurrence({
        featureId: record.featureId,
        displayName: record.displayName,
        reference: topologyReference as unknown as PuppyCAD.AssemblyTopologySelectionRef,
      });
      value.assemblyTopologyRef = nextReference;
      value.hostObjectId = nextReference.bodyBindingId;

      const objectIdEntry = Object.entries(LINEAR_COMPONENT_PATTERN_DESCRIPTOR_KEY_BY_OBJECT_ID_KEY)
        .find(([, candidateDescriptorKey]) => candidateDescriptorKey === descriptorKey);
      if (objectIdEntry) {
        state[objectIdEntry[0]] = nextReference.bodyBindingId;
      }
    });
  });
  return parsePuppyLinearComponentPatternExtension(document);
}

/**
 * 解析 `.puppy` 线性阵列定义扩展。
 *
 * @param value 未知 JSON 值。
 * @returns 已严格校验并克隆的定义文档。
 */
export function parsePuppyLinearComponentPatternExtension(
  value: unknown,
): LinearComponentPatternPersistenceDocument {
  return parseLinearComponentPatternPersistenceDocument(value);
}

/**
 * 从 assembly.json extensions 中读取线性阵列定义扩展。
 *
 * @param extensions assembly.json.extensions。
 * @returns 存在时返回定义文档；不存在时返回 `undefined`。
 */
export function readPuppyLinearComponentPatternExtensionFromAssemblyExtensions(
  extensions: Readonly<Record<string, unknown>> | undefined,
): LinearComponentPatternPersistenceDocument | undefined {
  const value = extensions?.[PUPPY_LINEAR_COMPONENT_PATTERN_EXTENSION_KEY];
  return value === undefined ? undefined : parsePuppyLinearComponentPatternExtension(value);
}

/**
 * 把线性阵列定义写入 assembly.json extensions。
 *
 * @param extensions 原始扩展字段。
 * @param document 线性阵列定义文档。
 * @returns 新扩展对象；没有任何扩展时返回 `undefined`。
 */
export function writePuppyLinearComponentPatternExtensionToAssemblyExtensions(
  extensions: Readonly<Record<string, unknown>> | undefined,
  document: LinearComponentPatternPersistenceDocument | undefined,
): Record<string, unknown> | undefined {
  const nextExtensions = extensions ? { ...extensions } : {};
  if (document) {
    nextExtensions[PUPPY_LINEAR_COMPONENT_PATTERN_EXTENSION_KEY] =
      parsePuppyLinearComponentPatternExtension(document);
  } else {
    delete nextExtensions[PUPPY_LINEAR_COMPONENT_PATTERN_EXTENSION_KEY];
  }
  return Object.keys(nextExtensions).length > 0 ? nextExtensions : undefined;
}

/**
 * 创建运行时对象 ID 到稳定装配节点 ID 的反向索引。
 *
 * @param runtimeIdsByAssemblyNodeId 正向装配映射。
 * @returns 运行时对象 ID 索引。
 */
function createStableObjectIdIndex(
  runtimeIdsByAssemblyNodeId: Readonly<Record<string, readonly string[]>>,
): ReadonlyMap<string, string> {
  const result = new Map<string, string>();
  Object.entries(runtimeIdsByAssemblyNodeId).forEach(([assemblyNodeId, runtimeObjectIds]) => {
    runtimeObjectIds.forEach((runtimeObjectId) => {
      if (!result.has(runtimeObjectId)) {
        result.set(runtimeObjectId, assemblyNodeId);
      }
    });
  });
  return result;
}

//------ 线性零部件阵列导入 BRep 稳定引用 开始------
/**
 * 将线性阵列定义中的 imported BRep 拓扑引用归一为离线包稳定身份。
 *
 * 设计说明：
 * imported BRep 的 `hostObjectId` 是 Worker 几何对象 ID，不属于 Three.js 场景对象；
 * 其权威身份必须由 `assemblyTopologyRef` 提供，并与本次导出的装配节点及 occurrence 级
 * `bodyBindingId` 同步。兼容字段 `hostObjectId` 也写为最终 bodyBindingId，避免运行时几何 ID
 * 泄漏到可跨会话恢复的定义中。
 *
 * @param source Manager 产生的线性阵列持久化文档。
 * @param resolveStableAssemblyNodeId 运行时装配节点 ID 到稳定节点 ID 的解析函数。
 * @param bodyBindingIndex 最终 display/body 绑定候选索引。
 * @returns imported BRep 拓扑引用已经稳定化的独立文档。
 */
function normalizePatternAssemblyTopologyReferences(
  source: LinearComponentPatternPersistenceDocument,
  resolveStableAssemblyNodeId: (runtimeObjectId: string) => string,
  bodyBindingIndex: PuppyBodyBindingReferenceIndex,
): LinearComponentPatternPersistenceDocument {
  const document = cloneLinearComponentPatternPersistenceDocument(source);
  document.records.forEach((record) => {
    const state = record.definition.state as unknown as Record<string, unknown>;
    Object.entries(state).forEach(([key, value]) => {
      if (!key.endsWith('Descriptor') || !isRecord(value)) {
        return;
      }

      const topologyReference = value.assemblyTopologyRef;
      if (!isRecord(topologyReference)) {
        return;
      }

      const sourceAssemblyNodeId = typeof topologyReference.assemblyNodeId === 'string'
        ? topologyReference.assemblyNodeId.trim()
        : '';
      const sourceBodyBindingId = typeof topologyReference.bodyBindingId === 'string'
        ? topologyReference.bodyBindingId.trim()
        : '';
      if (!sourceAssemblyNodeId || !sourceBodyBindingId) {
        // 字段完整性仍由统一持久化解析器校验；此处只负责可识别引用的导出归一化。
        return;
      }

      const stableAssemblyNodeId = resolveStableAssemblyNodeId(sourceAssemblyNodeId);
      const exportedBodyBindingId = resolvePuppyExportedBodyBindingId(bodyBindingIndex, {
        assemblyNodeId: stableAssemblyNodeId,
        bodyBindingId: sourceBodyBindingId,
        bodyObjectIds: [
          typeof topologyReference.bodyObjectId === 'string'
            ? topologyReference.bodyObjectId
            : null,
          typeof value.hostObjectId === 'string' ? value.hostObjectId : null,
        ],
      });
      if (!exportedBodyBindingId) {
        throw new Error(
          `线性零部件阵列 imported BRep 拓扑引用无法归一为离线包 bodyBindingId：`
          + `assemblyNodeId=${stableAssemblyNodeId}，bodyBindingId=${sourceBodyBindingId}。`,
        );
      }

      // 装配节点、bodyBinding 与兼容宿主字段必须指向同一份最终离线包身份。
      topologyReference.assemblyNodeId = stableAssemblyNodeId;
      topologyReference.bodyBindingId = exportedBodyBindingId;
      value.hostObjectId = exportedBodyBindingId;
    });
  });
  return parsePuppyLinearComponentPatternExtension(document);
}
//------ 线性零部件阵列导入 BRep 稳定引用 结束------

/**
 * 统一转换定义中的场景对象 ID。
 *
 * @param source 来源文档。
 * @param transformReferenceObjectId 普通场景引用 ID 转换函数。
 * @param transformComponentObjectId 组件源对象 ID 转换函数；缺省时与普通引用使用同一规则。
 * @returns 转换后的独立文档。
 */
function transformPatternObjectIds(
  source: LinearComponentPatternPersistenceDocument,
  transformReferenceObjectId: (objectId: string) => string,
  transformComponentObjectId: (objectId: string) => string = transformReferenceObjectId,
): LinearComponentPatternPersistenceDocument {
  const document = cloneLinearComponentPatternPersistenceDocument(source);
  document.records.forEach((record) => {
    const state = record.definition.state as unknown as Record<string, unknown>;

    // 源零部件是阵列定义的核心引用，必须逐项归一。
    state.componentObjectIds = (state.componentObjectIds as string[]).map(transformComponentObjectId);
    // 修改实例保存的是派生复制体 ID，重建后会产生新 ID，因此不能跨会话沿用。
    state.modifiedInstanceObjectIds = [];

    // SceneObject 描述符使用场景身份；imported BRep 拓扑宿主已由 assemblyTopologyRef 稳定化。
    Object.entries(state).forEach(([key, value]) => {
      if (!key.endsWith('Descriptor') || !isRecord(value)) {
        return;
      }
      if (typeof value.objectId === 'string' && value.objectId.length > 0) {
        value.objectId = transformReferenceObjectId(value.objectId);
      }
      if (
        isRecord(value.assemblyTopologyRef)
        && typeof value.assemblyTopologyRef.bodyBindingId === 'string'
      ) {
        // 节点别名重映射可能同步改写 occurrence 作用域，兼容宿主字段必须随权威引用更新。
        value.hostObjectId = value.assemblyTopologyRef.bodyBindingId;
        return;
      }
      if (
        typeof value.hostObjectId === 'string'
        && value.hostObjectId.length > 0
      ) {
        value.hostObjectId = transformReferenceObjectId(value.hostObjectId);
      }
    });

    LINEAR_COMPONENT_PATTERN_OBJECT_ID_KEYS.forEach((key) => {
      const objectId = state[key];
      if (typeof objectId !== 'string' || objectId.length === 0) {
        return;
      }

      const descriptor = state[LINEAR_COMPONENT_PATTERN_DESCRIPTOR_KEY_BY_OBJECT_ID_KEY[key]];
      if (
        isRecord(descriptor)
        && isRecord(descriptor.assemblyTopologyRef)
        && typeof descriptor.assemblyTopologyRef.bodyBindingId === 'string'
      ) {
        // imported BRep 的兼容对象字段与稳定拓扑引用保持同一 body occurrence 身份。
        state[key] = descriptor.assemblyTopologyRef.bodyBindingId;
        return;
      }
      if (isRecord(descriptor) && typeof descriptor.hostObjectId === 'string') {
        // 拓扑代理 UUID 是拾取期派生值；顶层兼容字段应退回到已经稳定化的拓扑宿主。
        state[key] = descriptor.hostObjectId;
        return;
      }
      state[key] = transformReferenceObjectId(objectId);
    });
  });
  return parsePuppyLinearComponentPatternExtension(document);
}

/**
 * 判断未知值是否为普通对象。
 *
 * @param value 待判断值。
 * @returns 是否可作为记录读取。
 */
function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === 'object' && !Array.isArray(value));
}
